import React from "react"

import { Tooltip } from "@coval/ui/tooltip"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/utils/cn"

interface HeaderCycleNavigationProps {
  displayIndex: number | null
  hasNext: boolean
  hasPrevious: boolean
  isLoading?: boolean
  navAriaLabel: string
  nextAriaLabel: string
  onNext: () => void
  onPortalSlotMount?: (element: HTMLDivElement | null) => void
  onPrevious: () => void
  previousAriaLabel: string
  totalItems: number
}

export const HeaderCycleNavigation: React.FC<HeaderCycleNavigationProps> = ({
  displayIndex,
  hasNext,
  hasPrevious,
  isLoading = false,
  navAriaLabel,
  nextAriaLabel,
  onNext,
  onPortalSlotMount,
  onPrevious,
  previousAriaLabel,
  totalItems,
}) => {
  // The bar renders in every state so the page actions in the portal slot
  // never depend on whether the current item was found in the list.
  const isPositionKnown = !isLoading && displayIndex != null && totalItems > 0
  const canGoPrevious = isPositionKnown && hasPrevious
  const canGoNext = isPositionKnown && hasNext

  return (
    <div className="flex items-center gap-2">
      <nav
        className="flex items-center gap-1.5 shrink-0 text-sm"
        aria-label={navAriaLabel}
        aria-busy={isLoading || undefined}
      >
        <Tooltip content={previousAriaLabel} triggerDisabled={!canGoPrevious}>
          <button
            type="button"
            onClick={onPrevious}
            disabled={!canGoPrevious}
            aria-label={previousAriaLabel}
            className={cn(
              "p-0.5 rounded transition-colors focus:outline-none",
              canGoPrevious
                ? "text-coval-text hover:bg-surface-1-hover cursor-pointer"
                : "text-coval-text-muted cursor-default",
            )}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </Tooltip>
        {isLoading ? (
          <div
            data-testid="header-cycle-navigation-loading"
            className="h-4 w-8 animate-pulse rounded bg-surface-2"
          />
        ) : (
          <span className="flex items-center gap-0.5">
            <span
              className={
                isPositionKnown
                  ? "text-coval-text font-medium"
                  : "text-coval-text-muted"
              }
            >
              {isPositionKnown ? displayIndex : "–"}
            </span>
            <span className="text-coval-text-muted">
              / {totalItems > 0 ? totalItems : "–"}
            </span>
          </span>
        )}
        <Tooltip content={nextAriaLabel} triggerDisabled={!canGoNext}>
          <button
            type="button"
            onClick={onNext}
            disabled={!canGoNext}
            aria-label={nextAriaLabel}
            className={cn(
              "p-0.5 rounded transition-colors focus:outline-none",
              canGoNext
                ? "text-coval-text hover:bg-surface-1-hover cursor-pointer"
                : "text-coval-text-muted cursor-default",
            )}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </Tooltip>
      </nav>
      {onPortalSlotMount ? (
        <div ref={onPortalSlotMount} className="flex items-center gap-1" />
      ) : null}
    </div>
  )
}
